import { NavLink, useLocation } from "react-router-dom";
import { useSidebar } from "../context/SidebarContext";
import { MOBILE_BOTTOM_NAV_PATHS } from "./mobileNavConfig";

type Tab = {
  to: string;
  label: string;
  /** Nombre del glifo de Material Symbols Rounded. */
  icon: string;
  /** Override opcional del tamaño del icono (algunos glifos rinden más
   *  chicos/grandes ópticamente). Default: text-[26px]. */
  iconSize?: string;
};

// Cada destino con el MISMO glifo que la sidebar de desktop —
// ADR-2026-08-04-los-iconos-de-la-barra-son-los-de-la-sidebar. `/redes-sociales/imagenes`
// era `history` acá y `photo_library` allá: el mismo lugar con dos íconos según de
// dónde se lo mirara.

// 4 tabs: Inicio = Interfaz V2, Modos, Creaciones, Mi marca. Había un set de 3 con
// "Estudio" (el flujo clásico) para el Grupo A y el grupo desconocido; se fue con el
// flujo (ADR-2026-10-28-se-borra-el-flujo-clasico).
const TABS: Tab[] = [
  // El glifo `home` rinde algo más chico, así que va un poco más grande.
  { to: "/interfaz-v2", label: "Inicio", icon: "home", iconSize: "text-[30px]" },
  { to: "/modos", label: "Modos", icon: "view_agenda" },
  { to: "/redes-sociales/imagenes", label: "Creaciones", icon: "photo_library" },
  { to: "/mi-marca", label: "Marca", icon: "shopping_bag" },
];

/**
 * Barra de navegación inferior para mobile. Solo se muestra en las
 * "pantallas-tab" (ver MOBILE_BOTTOM_NAV_PATHS). Reemplaza al menú hamburguesa
 * como navegación principal en mobile.
 *
 * Iconos (Material Symbols Rounded): con los ajustes de la familia (`FILL 1,
 * wght 400`), o sea los mismos que la sidebar de desktop, y el estado marcado sólo
 * por el color — ADR-2026-08-04-los-iconos-de-la-barra-son-los-de-la-sidebar.
 */
export const MobileBottomNav = () => {
  const location = useLocation();
  const { mobileBackOverride, mobileHideBottomNav } = useSidebar();

  // Solo visible en las pantallas raíz de cada tab.
  if (!MOBILE_BOTTOM_NAV_PATHS.includes(location.pathname)) return null;

  // En lugar de desmontarla de golpe, la barra se desliza hacia abajo (con un
  // fundido sutil) cuando:
  //  · hay un sub-flujo con su propio back (preset sheet de Interfaz V2 —capa
  //    z-[35]—, o la vista detalle del historial), o
  //  · la pantalla entra en "modo tarea" con su propia barra de acción fija
  //    (ej. selección de productos con el CTA de generar).
  // Así, al abrir una imagen ampliada en el historial, la nav se va con una
  // animación suave en vez de parpadear.
  const hidden = mobileBackOverride !== null || mobileHideBottomNav;


  return (
    <nav
      aria-hidden={hidden}
      className={`md:hidden select-none fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-black/[0.06] rounded-t-[22px] shadow-[0_-2px_16px_rgb(var(--brand-black-rgb)_/_0.06)] transition-[transform,opacity] duration-300 ease-out ${
        hidden
          ? "translate-y-full opacity-0 pointer-events-none"
          : "translate-y-0 opacity-100"
      }`}
      // `WebkitTouchCallout`/`userSelect` en none: al mantener apretado en iOS
      // ya no aparece el menú de selección (Copy/Look Up/Translate) ni se
      // seleccionan los íconos/labels. El feedback de "pressed" lo da el
      // `active:opacity-60` de cada tab.
      style={{
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
        userSelect: "none",
      }}
    >
      <div className="flex items-stretch h-16">
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end
            className="flex-1 flex flex-col items-center justify-center gap-1 active:opacity-60"
          >
            {({ isActive }) => {
              // Mismo par que la sidebar de desktop: azul de marca el seleccionado,
              // el gris del shell (negro al 60%) el resto. Sin clases de variación:
              // el glifo va relleno como allá.
              const color = isActive ? "text-brand-primary" : "text-black/60";
              return (
                <>
                  <span
                    className={`material-symbols-rounded leading-none transition-colors ${
                      tab.iconSize ?? "text-[26px]"
                    } ${color}`}
                  >
                    {tab.icon}
                  </span>
                  <span
                    className={`text-[11px] font-medium leading-none transition-colors ${color}`}
                  >
                    {tab.label}
                  </span>
                </>
              );
            }}
          </NavLink>
        ))}
      </div>
    </nav>
  );
};
